import type { Flow } from 'services/Flow'
import { Hub, Toast } from 'services'
import { FlowStateEnum, DebugSocketStateEnum } from 'enums'
import { i18n } from 'i18n'

export default {
  getOpenDebugSocket(
    { commit, state, dispatch },
    config: ?{ host: ?string, port: ?number, securePort: ?number }
  ): Promise {
    config = config || state.debugSocketConf
    return new Promise((resolve, reject) => {
      if (state.debugSocket && state.debugSocketState === DebugSocketStateEnum.OPENED) {
        resolve(state.debugSocket)
      } else {
        dispatch('closeDebugSocket')
        dispatch('openDebugSocket', config)
        state.debugSocket.on('open', () => resolve(state.debugSocket))
        state.debugSocket.on('close', () => reject(new Error('Debug socket lost connection')))
      }
    })
  },

  openDebugSocket(
    { commit, state, dispatch },
    config: { host: ?string, port: ?number, securePort: ?number }
  ): void {
    commit('openDebugSocket', config)
    state.debugSocket.on('open', () => {
      commit('setDebugSocketState', DebugSocketStateEnum.OPENED)
    })
    state.debugSocket.on('step', m => {
      commit('addLog', { runState: m.state, step: m.step })
      if (_.isNumber(m.state)) commit('setFlowDebugState', m.state)
    })
    state.debugSocket.on('message', m => {
      if (m.Error) {
        // This means manatee handled an exception during the flow execution
        commit('addLog', { runState: FlowStateEnum.STOPPED, step: `log.error ${m.Error}` })
        commit('setFlowDebugState', FlowStateEnum.STOPPED)
      }
    })
    state.debugSocket.on('state', m => {
      if (_.isNumber(m.state)) commit('setFlowDebugState', m.state)
    })
    state.debugSocket.on('finished', m => {
      // Format end result
      let endStep = null
      if (m.result && m.result.error) {
        endStep = `log.error Uncaught ${m.result.error.Value}`
      } else if (m.result && m.result.result && m.result.result.Value !== 'undefined') {
        endStep = `log.info Returned ${m.result.result.Value}`
      } else if (m.decision === 'decline') {
        endStep = `log.error Flow execution declined: ${m.reason}`
      }
      console.log('finished', m, endStep)
      if (endStep) commit('addLog', { runState: FlowStateEnum.STOPPED, step: endStep })
      commit('setFlowDebugState', FlowStateEnum.STOPPED)
    })
    state.debugSocket.on('close', m => {
      commit('setDebugSocketState', DebugSocketStateEnum.CLOSED)
      dispatch('closeDebugSocket')
    })
    state.debugSocket.on('error', m => {
      console.log('Error closeDebugSocket', m)
      dispatch('closeDebugSocket')
    })
  },
  closeDebugSocket({ commit }): void {
    commit('closeDebugSocket')
  },
  runCodeInEditor({ commit, state, dispatch, rootState }, isDebug): void {
    function run(code, sourcemap) {
      return dispatch('getOpenDebugSocket').then(() =>
        dispatch(isDebug ? 'debugFlow' : 'runFlow', {
          flow: {
            ...(rootState.flowEditor.flow || {}),
            code: code,
            sourcemap: JSON.stringify(sourcemap)
          },
          inputs: rootState.debugger.debugInputs,
          delay: rootState.debugger.debugDelay
        })
      )
    }

    const currentFlow = rootState.flowEditor.flow

    const codeToRun =
      rootState.flowEditor.selectedCodeInEditor.es5Code || rootState.flowEditor.codeInEditor.es5Code

    return (
      Promise.all([
        dispatch('flow/Get all', 'flow', { root: true }),
        dispatch('field/Get all', 'field', { root: true })
      ])
        .then(([allFlows, allFields]) => {
          const inlined = Hub.inline(
            currentFlow.name,
            codeToRun,
            currentFlow.application,
            allFlows,
            allFields
          )
          return inlined.code
        })
        .catch(e => {
          console.warn('Failed to inline code - falling back to non-inlined', e.i18n || e)
          Toast.warning(i18n.t('INLINE_ERROR_HEADER'), {
            remove: 10000,
            text: i18n.t('INLINE_ERROR_MESSAGE_FALLBACK', { error: ` ${e.i18n || e}` })
          })
          return codeToRun
        })
        // NOTE: Flow inlining does not provide sourcemap for its transformation, so we don't forward any sourcemap here
        .then(code => run(code, {}))
    )
  },
  runFlow(
    { commit, state, dispatch },
    {
      flow,
      inputs,
      delay,
      noTracerContext
    }: { flow: Flow, inputs: Array<Object>, delay: number, noTracerContext: boolean }
  ): void {
    if (state.flowDebugState === FlowStateEnum.STOPPED) {
      commit('clearLog')
    }
    state.debugSocket.runFlow(flow, inputs, delay, noTracerContext)
    commit('setFlowDebugState', FlowStateEnum.RUNNING)
  },
  debugFlow(
    { commit, state, dispatch },
    { flow, inputs, delay }: { flow: Flow, inputs: Array<Object>, delay: number }
  ): void {
    if (state.flowDebugState === FlowStateEnum.STOPPED) {
      commit('addLog', { runState: FlowStateEnum.STOPPED, step: 'RUN' })
    }
    state.debugSocket.debugFlow(flow, inputs, delay)
    commit('setFlowDebugState', FlowStateEnum.DEBUGGING)
  },
  pauseFlow({ state }): void {
    if (state.debugSocket) state.debugSocket.pauseFlow()
    else console.log('Debug socket is empty')
  },
  forwardFlow({ state }): void {
    if (state.debugSocket) state.debugSocket.forwardFlow()
    else console.log('Debug socket is empty')
  },
  stopFlow({ state }): void {
    if (state.debugSocket) state.debugSocket.stopFlow()
    else console.log('Debug socket is empty')
  }
}
